import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Tabs, Tab, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getData } from '../../api/dataSlice';
import { getDashboardRawData, dashboardFmtDate, dashboardParseDate, translateDetectionType, databaseDateToFilename } from '../Utilities';
import { setDashboardActions } from '../chatbot/ChatManager';
import { isMobile } from 'react-device-detect';
import DetectionFilters from './DetectionFilters';
import DetectionChart from './DetectionChart';
import WeatherFilters from './WeatherFilters';
import WeatherCharts from './WeatherCharts';
import DetectionViewer from './DetectionViewer';

const WEATHER_STREAMS = [
    { key: 'temp', color: '#e74c3c', i18nKey: 'pages.dashboard.temperature' },
    { key: 'precipitation', color: '#003366', i18nKey: 'pages.dashboard.precipitation' },
    { key: 'snowfall', color: '#95a5a6', i18nKey: 'pages.dashboard.snowfall' },
    { key: 'wind', color: '#27ae60', i18nKey: 'pages.dashboard.wind' },
    { key: 'humidity', color: '#8e44ad', i18nKey: 'pages.dashboard.humidity' },
]
const WEATHER_STREAM_KEYS = WEATHER_STREAMS.map(elem => elem.key)
const SUMMARY_SKIP_COLS = new Set(['image_filename', 'timestamp', 'archive_picture_id', 'num_persons', 'num_vehicles'])

function daysBetween(d1, d2) { return Math.round((d2 - d1) / 86400000) + 1 }

// format date to YYYY-MM-DDTHH:MM (extends dashboardFmtDate with time)
function fmtDateTime(date) {
    return dashboardFmtDate(date) + 'T' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
}

function getDetections(startString, endString, archiveSource, cameraId) {

    const { summaryData } = getDashboardRawData()
    if (!summaryData) return { error: 'Detection data not available' }

    const startDate = dashboardParseDate(startString)
    const endDate = dashboardParseDate(endString)
    const numDays = daysBetween(startDate, endDate)
    const isPerImage = numDays <= 1

    const filtered = summaryData.filter(elem => {
        const timestamp = new Date(elem.timestamp)
        const date = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate())
        return date >= startDate && date <= endDate
    })

    const skip = SUMMARY_SKIP_COLS
    const parentCols = filtered.length ? Object.keys(summaryData[0]).filter(elem => !skip.has(elem) && !elem.includes('*')) : []
    const subtypeCols = filtered.length ? Object.keys(summaryData[0]).filter(elem => elem.includes('*')) : []

    if (isPerImage) {
        // build lookup: filename -> summaryData row
        const summaryByFile = {}
        for (const elem of filtered) {
            if (elem.image_filename) summaryByFile[elem.image_filename] = elem
        }

        // build complete timeline from archive source (includes images without detections)
        const timeline = []
        if (archiveSource?.[0]) {
            const endBound = new Date(endDate.getTime() + 86400000)
            for (const elem of archiveSource[0]) {
                if (elem[0] < startDate || elem[0] > endBound) continue
                const camera = elem[1]?.[cameraId]
                if (!camera?.datum) continue
                const filename = databaseDateToFilename(camera.datum)
                timeline.push({ dateStr: fmtDateTime(elem[0]), filename, row: summaryByFile[filename] || null })
            }
        }

        // fallback: if no archive source, use summaryData only
        if (!timeline.length && filtered.length) {
            for (const elem of filtered) {
                if (!elem.image_filename) continue
                timeline.push({ dateStr: fmtDateTime(new Date(elem.timestamp)), filename: elem.image_filename, row: elem })
            }
            timeline.sort((a, b) => a.dateStr.localeCompare(b.dateStr))
        }

        if (!timeline.length) return { error: 'No data for this range' }

        const dates = timeline.map(elem => elem.dateStr)
        const dailyCounts = {}

        dailyCounts['person'] = timeline.map(elem => elem.row ? (elem.row.num_persons || 0) : 0)

        for (const parentCol of parentCols) {
            dailyCounts[parentCol] = timeline.map(elem => {
                if (!elem.row) return 0
                let val = elem.row[parentCol] || 0
                for (const subtypeCol of subtypeCols) {
                    if (subtypeCol.split('*')[0] === parentCol) val -= (elem.row[subtypeCol] || 0)
                }
                return Math.max(0, val)
            })
        }

        for (const subtypeCol of subtypeCols) {
            dailyCounts[subtypeCol] = timeline.map(elem => elem.row ? (elem.row[subtypeCol] || 0) : 0)
        }

        const typeTotals = {}
        for (const elem in dailyCounts) typeTotals[elem] = dailyCounts[elem].reduce((a, b) => a + b, 0)

        const allTypes = Object.keys(dailyCounts).filter(elem => typeTotals[elem] > 0)
        allTypes.sort((a, b) => typeTotals[b] - typeTotals[a])

        return {
            dates,
            object_types: allTypes,
            daily_counts: Object.fromEntries(allTypes.map(elem => [elem, dailyCounts[elem]])),
            type_totals: Object.fromEntries(allTypes.map(elem => [elem, typeTotals[elem]])),
            image_filenames: timeline.map(elem => elem.filename),
            resolution: 'per_image'
        }
    }

    // daily resolution — generate ALL dates in range (including days without detections)
    if (!filtered.length) return { error: 'No detection data for this range' }

    const binned = filtered.map(elem => {
        const timestamp = new Date(elem.timestamp)
        return { ...elem, _bin: dashboardFmtDate(new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate())) }
    })

    // generate continuous date range (startDate .. endDate)
    const bins = []
    const cursor = new Date(startDate)
    while (cursor <= endDate) {
        bins.push(dashboardFmtDate(cursor))
        cursor.setDate(cursor.getDate() + 1)
    }

    const groups = {}
    for (const elem of bins) groups[elem] = []
    for (const elem of binned) {
        if (groups[elem._bin]) groups[elem._bin].push(elem)
    }

    const dailyCounts = {}

    // person counts → key is 'person'
    dailyCounts['person'] = bins.map(elem => (groups[elem] || []).reduce((a, b) => a + (b.num_persons || 0), 0))

    // parent types (subtract promoted subtypes) - key = API column name
    for (const parentCol of parentCols) {
        dailyCounts[parentCol] = bins.map(bin => {
            const rows = groups[bin] || []
            let parentVal = rows.reduce((a, b) => a + (b[parentCol] || 0), 0)
            for (const subtypeCol of subtypeCols) {
                if (subtypeCol.split('*')[0] === parentCol) {
                    parentVal -= rows.reduce((a, b) => a + (b[subtypeCol] || 0), 0)
                }
            }
            return Math.max(0, parentVal)
        })
    }

    // promoted subtypes → key is the composite API key
    for (const elem of subtypeCols) {
        dailyCounts[elem] = bins.map(bin => (groups[bin] || []).reduce((a, b) => a + (b[elem] || 0), 0))
    }

    const typeTotals = {}
    for (const elem in dailyCounts) typeTotals[elem] = dailyCounts[elem].reduce((a, b) => a + b, 0)
    const allTypes = Object.keys(dailyCounts).filter(elem => typeTotals[elem] > 0)
    allTypes.sort((a, b) => typeTotals[b] - typeTotals[a])

    // pick one representative image filename per bin (for tooltip preview)
    const imageFilenames = {}
    for (const bin of bins) {
        let best = groups[bin][0], bestDiff = Infinity
        for (const elem of groups[bin]) {
            const diff = Math.abs(new Date(elem.timestamp).getHours() - 12)
            if (diff < bestDiff) {
                bestDiff = diff;
                best = elem
            }
        }
        imageFilenames[bin] = best?.image_filename || null
    }

    return {
        dates: bins,
        object_types: allTypes,
        daily_counts: Object.fromEntries(allTypes.map(elem => [elem, dailyCounts[elem]])),
        type_totals: Object.fromEntries(allTypes.map(elem => [elem, typeTotals[elem]])),
        image_filenames: bins.map(elem => imageFilenames[elem] || null),
        resolution: 'daily'
    }
}

function getWeather(startString, endString) {

    const { weatherHourly, weatherDaily } = getDashboardRawData()

    const startDate = dashboardParseDate(startString)
    const endDate = dashboardParseDate(endString)
    const numDays = daysBetween(startDate, endDate)

    if (numDays <= 14) {

        if (!weatherHourly) return { resolution: 'hourly', dates: [] }

        const filtered = weatherHourly.filter(elem => {
            const date = new Date(elem.timestamp)
            return date >= startDate && date < new Date(endDate.getTime() + 86400000)
        })

        return {
            resolution: 'hourly',
            dates: filtered.map(elem => {
                const date = new Date(elem.timestamp)
                return dashboardFmtDate(date) + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
            }),
            temp: filtered.map(elem => elem.temp || 0),
            precipitation: filtered.map(elem => elem.precipitation || 0),
            snowfall: filtered.map(elem => elem.snow || 0),
            wind_speed: filtered.map(elem => elem.windSpeed || 0),
            wind_gusts: filtered.map(elem => elem.windGusts || 0),
            humidity: filtered.map(elem => elem.humidity || 0),
            weather_description: filtered.map(elem => elem.weather || ''),
        }

    } else {

        if (!weatherDaily) return { resolution: 'daily', dates: [] }

        const filtered = weatherDaily.filter(elem => {
            const date = new Date(elem.date)
            return date >= startDate && date <= endDate
        })

        return {
            resolution: 'daily',
            dates: filtered.map(elem => dashboardFmtDate(new Date(elem.date))),
            temp_max: filtered.map(elem => elem.temp_max || 0),
            temp_min: filtered.map(elem => elem.temp_min || 0),
            temp_avg: filtered.map(elem => elem.temp_avg || 0),
            precipitation: filtered.map(elem => elem.precipitation_sum || 0),
            snowfall: filtered.map(elem => elem.snow_sum || 0),
            wind_max: filtered.map(elem => elem.windSpeed_max || 0),
            wind_avg: filtered.map(elem => elem.windSpeed_avg || 0),
            wind_gusts: filtered.map(elem => elem.windGusts_max || 0),
            humidity: filtered.map(elem => elem.humidity_avg || 0),
            weather_description: filtered.map(elem => elem.weather || '')
        }
    }
}

export default function DashboardContent({ dashboardState, contentHeight, contentWidth }) {

    const { t } = useTranslation()
    const { CAMERAS, BASEURL } = window.conf
    const { source } = useSelector(getData)

    const [activeTab, setActiveTab] = useState('detections')
    const [detectionData, setDetectionData] = useState(null)
    const [weatherData, setWeatherData] = useState(null)
    const [enabledTypes, setEnabledTypes] = useState(new Set())
    const [weatherStreams, setWeatherStreams] = useState(new Set(WEATHER_STREAM_KEYS))
    const [viewerDate, setViewerDate] = useState(null)
    const [viewerTimestamp, setViewerTimestamp] = useState(null)
    const [viewerInitialType, setViewerInitialType] = useState(null)
    const [loading, setLoading] = useState(false)
    const [filtersHeight, setFiltersHeight] = useState(0)

    const observerRef = useRef(null)
    const stateRef = useRef({})
    const pendingTypesRef = useRef(undefined)
    const cameraId = dashboardState?.selectedCamera ?? CAMERAS?.[0]?.id ?? 0
    const baseUrl = `${BASEURL}cam${cameraId + 1}/zeitraffer/`
    const chartOverhead = isMobile ? 150 : 200 // tabs + padding + margins
    const chartHeight = contentHeight ? Math.max(250, contentHeight - filtersHeight - chartOverhead) : 500

    const tType = useCallback((key) => translateDetectionType(key, t), [t])

    // keep ref in sync for chatbot actions (avoids stale closures)
    useEffect(() => {
        stateRef.current = { activeTab, enabledTypes, weatherStreams, detectionData, viewerDate, tType }
    }, [activeTab, enabledTypes, weatherStreams, detectionData, viewerDate, tType])

    // expose dashboardActions for the chatbot (stable object, reads from ref)
    useEffect(() => {
        if (!dashboardState?.dataReady) return
        const actions = {
            getActiveTab: () => stateRef.current.activeTab,
            getEnabledDetectionTypes: () => {
                const types = stateRef.current.enabledTypes
                return types ? [...types].map(elem => stateRef.current.tType(elem)) : []
            },
            getDetectionTypes: () => {
                const data = stateRef.current.detectionData
                return data?.object_types ? data.object_types.map(elem => stateRef.current.tType(elem)) : []
            },
            getDateRange: () => {
                const dateRange = dashboardState?.dateRange
                if (!dateRange?.[0] || !dateRange?.[1]) return {}
                const fmt = d => d.format('DD.MM.YY')
                return { start: fmt(dateRange[0]), end: fmt(dateRange[1]) }
            },
            getWeatherStreams: () => {
                const streams = stateRef.current.weatherStreams
                return streams ? [...streams] : []
            },
            getDataLoaded: () => stateRef.current.detectionData !== null,
            getViewerDate: () => stateRef.current.viewerDate || null,
            showOnlyDetections: (deNames) => {
                // deNames are German display names — reverse-translate to API keys
                const data = stateRef.current.detectionData
                if (!data) return
                const matched = new Set()
                for (const deName of deNames) {
                    for (const key of data.object_types) {
                        if (stateRef.current.tType(key) === deName) { matched.add(key); break }
                    }
                }
                if (matched.size) setEnabledTypes(matched)
            },
            showAllDetections: () => {
                const data = stateRef.current.detectionData
                if (data) setEnabledTypes(new Set(data.object_types))
            },
            switchTab: (tab) => setActiveTab(tab),
            showWeatherStreams: (streams) => {
                setWeatherStreams(new Set(streams))
                setActiveTab('weather')
            },
            setDates: (start, end, typesOverride) => {
                // parse DD.MM.YY or DD.MM.YYYY to YYYY-MM-DD for onSetDateRange
                const parse = (s) => {
                    const parts = s.split('.')
                    if (parts.length !== 3) return null
                    let year = parts[2]
                    if (year.length === 2) year = '20' + year
                    return `${year}-${parts[1]}-${parts[0]}`
                }

                const startIso = parse(start)
                const endIso = parse(end)

                // store pending types so the data reload effect can apply them after data arrives
                if (typesOverride !== undefined) {
                    pendingTypesRef.current = typesOverride
                }

                if (startIso && endIso && dashboardState?.onSetDateRange) {
                    dashboardState.onSetDateRange(startIso, endIso)
                }

                // apply types directly if no date change triggers a reload
                if (!startIso || !endIso) {
                    if (typesOverride && Array.isArray(typesOverride)) {
                        actions.showOnlyDetections(typesOverride)
                    } else if (typesOverride === null) {
                        actions.showAllDetections()
                    }
                    pendingTypesRef.current = undefined
                }
            },
            resetView: () => {
                setActiveTab('detections')
                setWeatherStreams(new Set(WEATHER_STREAM_KEYS))
                const data = stateRef.current.detectionData
                if (data) setEnabledTypes(new Set(data.object_types))
            },
            openViewer: (date, timestamp, deTypeNames) => {
                if (!date) return
                setActiveTab('detections')
                if (dashboardState?.onDrillDown) dashboardState.onDrillDown(date) // drill down to single day
                setViewerDate(dashboardFmtDate(dashboardParseDate(date))) // normalize to YYYY-MM-DD (viewer compares against this format)
                setViewerTimestamp(timestamp || null)
                if (deTypeNames?.length) actions.showOnlyDetections(deTypeNames)
            },
        }
        setDashboardActions(actions)
        return () => setDashboardActions(null)
    }, [dashboardState?.dataReady, dashboardState?.dateRange]) // eslint-disable-line react-hooks/exhaustive-deps

    // compute ALL known types from the full dataset (once), so we can show inactive types grayed out
    const allKnownTypes = useMemo(() => {
        const { summaryData } = getDashboardRawData()
        if (!summaryData?.length) return []
        const skip = SUMMARY_SKIP_COLS
        const types = new Set()
        types.add('person')
        for (const elem of Object.keys(summaryData[0])) {
            if (!skip.has(elem)) types.add(elem)
        }
        return [...types]
    }, [dashboardState?.dataReady]) // eslint-disable-line react-hooks/exhaustive-deps

    // callback ref: attach ResizeObserver whenever the filters DOM element mounts (incl. after tab switch)
    const filtersRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null
        }
        if (node) {
            const observer = new ResizeObserver(elem => setFiltersHeight(elem[0].contentRect.height))
            observer.observe(node)
            observerRef.current = observer
        } else {
            setFiltersHeight(0)
        }
    }, [])

    useEffect(() => {
        if (!dashboardState?.dataReady || !dashboardState?.dateRange) return
        const [start, end] = dashboardState.dateRange
        if (!start || !end) return

        setLoading(true)

        const startString = dashboardFmtDate(start.toDate())
        const endString = dashboardFmtDate(end.toDate())
        const detections = getDetections(startString, endString, source, cameraId)

        if (!detections.error) {
            setDetectionData(detections)

            const pending = pendingTypesRef.current
            pendingTypesRef.current = undefined

            if (pending !== undefined) {
                // chatbot requested a type filter alongside the date change — apply it
                if (Array.isArray(pending)) {
                    const matched = new Set()
                    for (const deName of pending) {
                        for (const key of detections.object_types) {
                            if (tType(key) === deName) { matched.add(key); break }
                        }
                    }
                    setEnabledTypes(matched.size > 0 ? matched : new Set(detections.object_types))
                } else if (pending === null) {
                    setEnabledTypes(new Set(detections.object_types))
                }
            } else {
                setEnabledTypes(prev => {
                    // single-day view (drill-down) - keep selected filters intersected with available types
                    if (prev.size > 0) {
                        const intersection = new Set(detections.object_types.filter(elem => prev.has(elem)))
                        return intersection.size > 0 ? intersection : new Set(detections.object_types)
                    }
                    return new Set(detections.object_types)
                })
            }

        } else {
            setDetectionData(null)
            setEnabledTypes(new Set())
        }

        setWeatherData(getWeather(startString, endString))
        setLoading(false)
    }, [dashboardState?.dataReady, dashboardState?.dateRange, dashboardState?.selectedCamera]) // eslint-disable-line react-hooks/exhaustive-deps

    const toggleType = useCallback((type) => {
        setEnabledTypes(prev => {
            const next = new Set(prev)
            next.has(type) ? next.delete(type) : next.add(type)
            return next
        })
    }, [])

    const activeWeatherStreams = useMemo(() => {
        if (!weatherData || !weatherData.dates?.length) return new Set()
        const hasAny = (arr) => arr?.some(v => v !== 0 && v !== '')
        const isHourly = weatherData.resolution === 'hourly'
        const active = new Set()
        if (hasAny(isHourly ? weatherData.temp : weatherData.temp_max)) active.add('temp')
        if (hasAny(weatherData.precipitation)) active.add('precipitation')
        if (hasAny(weatherData.snowfall)) active.add('snowfall')
        if (hasAny(isHourly ? weatherData.wind_speed : weatherData.wind_max)) active.add('wind')
        if (hasAny(weatherData.humidity)) active.add('humidity')
        return active
    }, [weatherData])

    const toggleWeatherStream = useCallback((key) => {
        setWeatherStreams(prev => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }, [])

    const handleDayZoom = useCallback((dateString) => {
        dashboardState?.onDrillDown?.(dateString)
    }, [dashboardState?.onDrillDown]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleBarClick = useCallback((dateString, timestamp, clickedType) => {
        setViewerDate(dateString)
        setViewerTimestamp(timestamp || null)
        setViewerInitialType(clickedType || null)
    }, [])

    const handleViewerClose = useCallback(() => {
        setViewerDate(null)
        setViewerTimestamp(null)
        setViewerInitialType(null)
    }, [])

    return (
        <Box sx={{
            display: 'block',
            width: contentWidth,
            height: contentHeight,
            overflow: 'auto',
            background: '#f5f6fa',
            transition: '0.2s linear'
        }}>
            {!dashboardState?.dataReady ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography color="text.secondary">{t('pages.dashboard.loading_data')}</Typography>
                </Box>
            ) : (
                <Box sx={{ p: isMobile ? 1 : 2, height: '100%', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, value) => setActiveTab(value)}
                        sx={{
                            mb: isMobile ? 1 : 2, bgcolor: 'white', borderRadius: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                            minHeight: isMobile ? 36 : 48,
                            '& .MuiTab-root': { fontWeight: 600, fontSize: 14, textTransform: 'none', minHeight: isMobile ? 36 : 48 },
                            '& .Mui-selected': { color: '#003366' },
                            '& .MuiTabs-indicator': { backgroundColor: '#003366', height: 3 }
                        }}
                    >
                        <Tab label={t('pages.dashboard.tab_detections')} value="detections" />
                        <Tab label={t('pages.dashboard.tab_weather')} value="weather" />
                    </Tabs>

                    {activeTab === 'detections' && (
                        <Box>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) :
                                detectionData ? (
                                    <>
                                        <Box ref={filtersRef}>
                                            <DetectionFilters
                                                objectTypes={allKnownTypes}
                                                activeTypes={detectionData.object_types}
                                                enabledTypes={enabledTypes}
                                                typeTotals={detectionData.type_totals}
                                                onToggle={toggleType}
                                                onSolo={(type) => setEnabledTypes(new Set([type]))}
                                                onAll={() => setEnabledTypes(new Set(detectionData.object_types))}
                                                onNone={() => setEnabledTypes(new Set())}
                                                translateType={tType}
                                                totalDetections={Object.values(detectionData.type_totals).reduce((a, b) => a + b, 0)}
                                                imageCount={detectionData.dates?.length || 0}
                                            />
                                        </Box>
                                        <DetectionChart
                                            data={detectionData}
                                            enabledTypes={enabledTypes}
                                            onDayZoom={handleDayZoom}
                                            onBarClick={handleBarClick}
                                            baseUrl={baseUrl}
                                            translateType={tType}
                                            chartHeight={chartHeight}
                                        />
                                    </>
                                ) : (
                                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                                        {t('pages.dashboard.no_data')}
                                    </Typography>
                                )
                            }
                        </Box>
                    )}

                    {activeTab === 'weather' && (
                        <Box>
                            <WeatherFilters
                                streamDefs={WEATHER_STREAMS}
                                streams={weatherStreams}
                                activeStreams={activeWeatherStreams}
                                onToggle={toggleWeatherStream}
                                onSolo={(key) => setWeatherStreams(new Set([key]))}
                            />
                            {weatherData && weatherData.dates?.length > 0 ? (
                                <WeatherCharts
                                    weatherData={weatherData}
                                    streams={new Set([...weatherStreams].filter(elem => activeWeatherStreams.has(elem)))}
                                />
                            ) : (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                                    {t('pages.dashboard.no_data')}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {viewerDate && (
                        <DetectionViewer
                            date={viewerDate}
                            onClose={handleViewerClose}
                            initialEnabledTypes={enabledTypes}
                            initialTimestamp={viewerTimestamp}
                            initialClickedType={viewerInitialType}
                            baseUrl={baseUrl}
                            translateType={tType}
                            cameraId={cameraId}
                        />
                    )}
                </Box>
            )}
        </Box>
    )
}
